// EditLoglineModal.js
import React, { useState, useEffect } from 'react';
import { toast } from 'react-toastify';
import { router } from '@inertiajs/react';

const EditLoglineModal = ({ isOpen, onClose, script, updateUrl }) => {
  const [logline, setLogline] = useState('');
  const [saving, setSaving] = useState(false);
  const [loglineOptions, setLoglineOptions] = useState([]);

  useEffect(() => {
    if (script && isOpen) {
      setLoglineOptions(script.loglines || []);
      
      // Get current logline
      const currentLogline = script.currentLogline || 
                           script.profile_settings?.logline || 
                           script.logline || 
                           script.loglines?.[0] || '';
      
      setLogline(currentLogline);
    }
  }, [script, isOpen]);

  const handleLoglineDropdownChange = (e) => {
    const selectedLogline = e.target.value;
    if (selectedLogline) {
      setLogline(selectedLogline);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    
    try {
      const response = await fetch(`${updateUrl}${script.hash}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-TOKEN': document.querySelector('meta[name="csrf-token"]').getAttribute('content'),
        },
        body: JSON.stringify({
          logline: logline.trim(),
          // Include required fields to satisfy validation
          is_public: true,
          allow_comments: script.profile_settings?.allow_comments ?? true,
          allow_ratings: script.profile_settings?.allow_ratings ?? true,
          allow_connection_requests: script.profile_settings?.allow_connection_requests ?? true,
          is_featured: script.profile_settings?.is_featured ?? true,
          disclosure_level: script.profile_settings?.disclosure_level ?? 'partial',
        }),
      });

      if (response.ok) {
        toast.success('Logline updated!');
        onClose();
        
        // Simple page refresh - guaranteed to work
        setTimeout(() => {
          window.location.reload();
        }, 500);
      } else {
        throw new Error('Failed to save logline');
      }
    } catch (error) {
      console.error('Save logline error:', error);
      toast.error('Failed to save logline');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    onClose();
  };

  if (!isOpen || !script) return null;

  return (
    <div className="modal d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }} tabIndex="-1">
      <div className="modal-dialog">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Edit Logline: {script.title}</h5>
            <button type="button" className="btn-close" onClick={handleCancel} aria-label="Close"></button>
          </div>
          
          <div className="modal-body">
            {/* Existing loglines dropdown */}
            {loglineOptions.length > 0 && (
              <div className="mb-3">
                <label className="form-label small fw-semibold">Quick select from existing:</label>
                <select
                  className="form-select"
                  defaultValue=""
                  onChange={handleLoglineDropdownChange}
                >
                  <option value="" disabled>— Choose an existing logline —</option>
                  {loglineOptions.map((option, idx) => (
                    <option key={idx} value={option}>
                      {option.length > 80 ? option.slice(0, 77) + '…' : option}
                    </option>
                  ))}
                </select>
                <div className="form-text">Selecting from dropdown will replace the text below</div>
              </div>
            )}

            {/* Editable logline textarea */}
            <div className="mb-3">
              <label className="form-label fw-semibold">Logline for your portfolio:</label>
              <textarea
                className="form-control"
                rows="4"
                value={logline}
                onChange={(e) => setLogline(e.target.value)}
                placeholder="Enter or edit your logline here..."
                style={{ resize: 'vertical' }}
                maxLength="500"
              />
              <div className="form-text d-flex justify-content-between">
                <span>This will appear on your portfolio</span>
                <span className="text-muted">{logline.length}/500</span>
              </div>
            </div>
          </div>
          
          <div className="modal-footer">
            <button 
              type="button" 
              className="btn btn-secondary" 
              onClick={handleCancel}
              disabled={saving}
            >
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary" 
              onClick={handleSave}
              disabled={saving || !logline.trim()}
            >
              {saving ? (
                <>
                  <div className="spinner-border spinner-border-sm me-2" role="status">
                    <span className="visually-hidden">Saving...</span>
                  </div>
                  Saving...
                </>
              ) : (
                <>
                  <i className="fa fa-save me-1"></i>
                  Save Logline
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default EditLoglineModal;